import React, { useEffect, useState, FC, memo } from 'react';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import ru from 'date-fns/locale/ru';

import useKeyDownCallback from 'toolkit/useKeyDownCallback';
import { KeyboardEventCode } from 'toolkit/types';
import useScrollOutsideCallback from 'toolkit/useScrollOutsideCallback';
import { mobileQueryList, useMediaQueryChangeListener } from 'toolkit/useMedia';

import IconXCircle from 'ui/icons/XCircle';

import CustomInput from '../CustomInput';
import { renderDateInputHeader } from '../DatePickerHeader';
import { RenderHeaderMode } from '../DatePickerHeader/types';
import { getRenderDayRangeContents } from '../DayRenderer';
import DatePickerCalendarContainer from '../DatePickerCalendarContainer';
import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE } from '../consts';
import { DatePickerCalendarContainerProps } from '../DatePickerCalendarContainer/types';

import 'react-datepicker/dist/react-datepicker.css';

import styles from './index.module.scss';

interface DateRangeInputProps {
  elementId: string;
  startDate: Date | null;
  endDate: Date | null;
  label?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  isDisabled?: boolean;
  showPreviousMonths?: boolean;
  isLoading?: boolean;
  popperPlacement?:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';
  calendarClassName?: string;
  inputClassName?: string;
  calendarButtonClassName?: string;
  popperClassName?: string;
  clearButtonClassName?: string;
  clearIcon?: React.ReactNode;
  containerClassName?: string;
  autoComplete?: string;
  placeholder?: string;
  dateFormat?: string;
  openToDate?: Date;
  monthsShown?: number;
  isClearable?: boolean;
  focusSelectedMonth?: boolean;
  withPortal?: boolean;
  inline?: boolean;
  onChange?: (startDate: Date | null, endDate: Date | null, event?: React.SyntheticEvent<any> | undefined) => void;
  onError?: (err: { code: number; msg: string }) => void;
  onEnterPressed?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const DateRangeInput: FC<DateRangeInputProps> = ({
  elementId,
  startDate: propStartDate,
  endDate: propEndDate,
  label,
  minDate,
  maxDate,
  isDisabled,
  isLoading,
  showPreviousMonths = false,
  dateFormat = 'dd.MM.yyyy',
  placeholder = 'дд.мм.гггг - дд.мм.гггг ',
  popperPlacement = 'right',
  calendarClassName,
  inputClassName,
  calendarButtonClassName,
  popperClassName,
  clearButtonClassName,
  clearIcon,
  containerClassName,
  autoComplete,
  openToDate,
  monthsShown: initialMonthsShown,
  isClearable = false,
  focusSelectedMonth = true,
  withPortal = false,
  inline = false,
  onChange,
  onError,
  onEnterPressed,
}) => {
  const isMobile = useMediaQueryChangeListener(mobileQueryList);

  const [ref, setRef] = useState<any>(null);

  const [rangeStartDate, setRangeStartDate] = useState<Date | null>(propStartDate);
  const [rangeEndDate, setRangeEndDate] = useState<Date | null>(propEndDate);

  const currentMinDate = minDate || DEFAULT_MIN_DATE;
  const currentMaxDate = maxDate || DEFAULT_MAX_DATE;
  const resolvedClearIcon = clearIcon || <IconXCircle />;
  const shouldRenderCustomClearButton = isClearable && Boolean(resolvedClearIcon);

  const handleRangeChange = (dates: [Date | null, Date | null], event?: React.SyntheticEvent<any> | undefined) => {
    const [startDate, endDate] = dates;

    setRangeStartDate(startDate);
    setRangeEndDate(endDate);
    onChange?.(startDate, endDate, event);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRangeStartDate(null);
    setRangeEndDate(null);
    onChange?.(null, null, event);
  };

  const onKeyDown = useKeyDownCallback<React.KeyboardEvent<HTMLElement>>(
    (event) => onEnterPressed?.(event as React.KeyboardEvent<HTMLInputElement>),
    {
      include: [KeyboardEventCode.ENTER],
    }
  );

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isMobile) {
      e.target.blur();
    }
  };

  const onClose = () => {
    ref?.setOpen(false);
  };

  useEffect(() => {
    setRangeStartDate(propStartDate);
  }, [propStartDate]);

  useEffect(() => {
    setRangeEndDate(propEndDate);
  }, [propEndDate]);

  useScrollOutsideCallback(ref, () => {
    if (!isMobile) {
      onClose();
    }
  });

  const CalendarContainer = ({ className, children }: DatePickerCalendarContainerProps) => (
    <DatePickerCalendarContainer
      className={className}
      htmlFor={elementId}
      label={label}
      onClose={() => {
        ref?.setOpen(false);
      }}>
      {children}
    </DatePickerCalendarContainer>
  );

  return (
    <DatePicker
      id={elementId}
      title={label}
      locale={ru}
      selected={rangeStartDate}
      startDate={rangeStartDate}
      endDate={rangeEndDate}
      minDate={currentMinDate}
      maxDate={currentMaxDate}
      disabled={isDisabled || isLoading}
      onChange={handleRangeChange}
      autoComplete={autoComplete}
      wrapperClassName={classNames(styles.DatePicker, containerClassName)}
      ref={(r) => setRef(r)}
      calendarClassName={classNames(styles.DatePickerCalendar, calendarClassName)}
      popperClassName={popperClassName}
      dayClassName={() => styles.DatePickerCalendarDay}
      weekDayClassName={() => styles.DatePickerCalendarWeekDay}
      placeholderText={placeholder}
      customInput={React.createElement(CustomInput, {
        id: `${elementId}_date-picker-wrapper`,
        inputClassName,
        calendarButtonClassName,
        clearButtonClassName,
        clearIcon: resolvedClearIcon,
        isClearable: shouldRenderCustomClearButton,
        onClear: handleClear,
      })}
      strictParsing
      showPreviousMonths={isMobile && showPreviousMonths}
      renderDayContents={getRenderDayRangeContents(rangeStartDate, rangeEndDate)}
      renderCustomHeader={renderDateInputHeader(RenderHeaderMode.SINGLE, isMobile)}
      dateFormat={dateFormat}
      withPortal={withPortal}
      openToDate={openToDate}
      inline={inline}
      monthsShown={initialMonthsShown}
      popperPlacement={popperPlacement}
      calendarContainer={isMobile ? CalendarContainer : undefined}
      showMonthDropdown
      showYearDropdown
      selectsRange
      focusSelectedMonth={focusSelectedMonth}
      isClearable={isClearable && !shouldRenderCustomClearButton}
      onInputError={onError}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
    />
  );
};

export default memo(DateRangeInput);
